export interface ScoreCardItem {
  label: string;
  value: number | string | undefined;
  comment?: string;
  /** Shown when value is undefined. Defaults to 'N/A' */
  fallback?: string;
  /** Suffix appended to numeric values e.g. '/10'. Not appended to fallback. */
  suffix?: string;
  /** When set, render this card in the metric variant with a --perf-* tier
   *  accent (value tinted high/med/low, neutral keeps the primary color).
   *  Omit for a plain fact card (value + CAPS label, no performance color). */
  perf?: 'high' | 'med' | 'low' | 'neutral';
}

export interface ScoreCardsProps {
  items: ScoreCardItem[];
  className?: string;
}

export function ScoreCards({ items, className }: ScoreCardsProps) {
  // Value on top, CAPS label below, on an auto-fill grid. Items with `perf`
  // render as metric cards (tier-colored); the rest are neutral fact cards.
  return (
    <div className={`stat-cards stat-cards--auto ${className || ''}`}>
      {items.map((item, idx) => {
        const isEmpty = item.value === undefined;
        const display = !isEmpty
          ? `${item.value}${item.suffix || ''}`
          : (item.fallback ?? 'N/A');
        const variantClass = item.perf ? 'stat-card--metric' : 'stat-card--fact';
        // A metric card with no value is de-emphasized (muted grey), not tier-
        // colored — blue "neutral" on an empty card reads as a highlight.
        const emptyClass = item.perf && isEmpty ? 'stat-card--empty' : '';
        return (
          <div key={idx} className={`stat-card ${variantClass} ${emptyClass}`.trim()} data-perf={item.perf}>
            <span className="stat-card-value">{display}</span>
            <span className="stat-card-label">{item.label}</span>
            {item.comment && <span className="stat-card-comment">{item.comment}</span>}
          </div>
        );
      })}
    </div>
  );
}
